import { useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, ChevronRight, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCompactTime } from "@/lib/timeUtils";
import { format, parseISO, isToday, isWeekend } from "date-fns";
import { SectionHelp } from "@/components/help";
import DailyQuickLog from "./DailyQuickLog";

interface EffortSummaryData {
  thisWeekLogged: number;
  thisWeekExpected: number;
  lastWeekLogged: number;
  variance: number;
  last7Days?: { date: string; hours: number }[];
}

interface EffortSummaryWidgetProps {
  effort: EffortSummaryData;
}

export default function EffortSummaryWidget({ effort }: EffortSummaryWidgetProps) {
  const [, navigate] = useLocation();
  const [showQuickLog, setShowQuickLog] = useState(false);

  const days = effort.last7Days ?? [];
  const totalLast7 = days.reduce((sum, d) => sum + d.hours, 0);
  const maxHours = Math.max(8, ...days.map(d => d.hours));

  return (
    <>
      <Card className="h-full border-0 shadow-lg bg-card">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <SectionHelp contentId="dashboard:effort" tooltip="About effort tracking">
                <CardTitle className="text-lg font-bold">Last 7 Days</CardTitle>
              </SectionHelp>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 gap-1 font-semibold h-8 px-2"
              onClick={() => navigate("/timesheet")}
            >
              Timesheet <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <div className="space-y-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground font-medium">Total logged</span>
              <span className="text-2xl font-bold text-foreground">{formatCompactTime(totalLast7)}</span>
            </div>

            {days.length === 7 ? (
              <div className="grid grid-cols-7 gap-1.5">
                {days.map((day) => {
                  const date = parseISO(day.date);
                  const heightPct = maxHours > 0 ? (day.hours / maxHours) * 100 : 0;
                  const today = isToday(date);
                  const weekend = isWeekend(date);
                  const empty = day.hours === 0;
                  return (
                    <div key={day.date} className="flex flex-col items-center gap-1.5">
                      <div className="relative h-16 w-full bg-muted rounded-md overflow-hidden flex items-end">
                        <div
                          className={cn(
                            "w-full rounded-md transition-all duration-700 ease-out",
                            empty
                              ? "bg-transparent"
                              : weekend
                                ? "bg-gradient-to-t from-slate-400 to-slate-300 dark:from-slate-600 dark:to-slate-500"
                                : "bg-gradient-to-t from-emerald-500 to-teal-400",
                          )}
                          style={{ height: `${Math.max(heightPct, empty ? 0 : 8)}%` }}
                        />
                        {today && (
                          <span className="absolute top-0.5 right-0.5 text-[8px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide">
                            Now
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-medium uppercase">
                        {format(date, 'EEE')}
                      </div>
                      <div className={cn(
                        "text-xs font-semibold tabular-nums",
                        empty ? "text-muted-foreground/60" : "text-foreground",
                      )}>
                        {day.hours > 0 ? formatCompactTime(day.hours) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground">
                Loading effort data…
              </div>
            )}

            <Button
              onClick={() => setShowQuickLog(true)}
              className="w-full gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-sm"
            >
              <Zap className="h-4 w-4" />
              Log effort for today
            </Button>
          </div>
        </CardContent>
      </Card>
      <DailyQuickLog open={showQuickLog} onOpenChange={setShowQuickLog} />
    </>
  );
}
